
import React, { useState } from 'react';
import { UserProfile, ViewState } from '../types.ts';
import { Utensils, CheckSquare, Heart, Wallet, GraduationCap, Map, ArrowRight, Sparkles, LayoutGrid, Zap, Sunrise, Video, BarChart2, Scroll, Compass, ChevronLeft, Loader2, Send, ChefHat, Dumbbell, Plane, Target, ListChecks, Info } from 'lucide-react';
import { TRANSLATIONS } from '../translations.ts';
import { generateRecipe, breakdownTask, generateHealthPlan, analyzeFinances, generateTravelPlan } from '../services/geminiService.ts';

interface Props {
  user: UserProfile;
  onNavigate?: (view: ViewState) => void;
}

type ModuleType = 'cooking' | 'tasks' | 'health' | 'finance' | 'learning' | 'travel' | null;

const ModulesHub: React.FC<Props> = ({ user, onNavigate }) => {
  const [activeModule, setActiveModule] = useState<ModuleType>(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const t = TRANSLATIONS[user.language];

  const handleToolAction = async () => {
    if (!inputValue.trim() && activeModule !== 'health') return;
    setIsLoading(true);
    setResult(null);
    try {
      let aiResponse: any;
      switch (activeModule) {
        case 'cooking':
          aiResponse = await generateRecipe(user, inputValue);
          break;
        case 'tasks':
          aiResponse = await breakdownTask(user, inputValue);
          break;
        case 'health':
          aiResponse = await generateHealthPlan(user);
          break;
        case 'finance':
          aiResponse = await analyzeFinances(user, 5000, [{ label: 'Expense', amount: inputValue }]);
          break;
        case 'travel':
          aiResponse = await generateTravelPlan(user, inputValue);
          break;
        case 'learning':
          if (onNavigate) onNavigate(ViewState.LEARNING);
          return;
      }
      setResult(aiResponse);
    } catch (e) {
      console.error(e);
      setResult({ error: true });
    } finally {
      setIsLoading(false);
    }
  };

  const coreModules = [
    { id: 'cooking', label: t.cooking_tool, icon: Utensils, color: 'text-orange-500', bg: 'bg-orange-100', desc: t.cooking_desc },
    { id: 'tasks', label: t.tasks_tool, icon: CheckSquare, color: 'text-blue-500', bg: 'bg-blue-100', desc: t.tasks_tool_desc },
    { id: 'health', label: t.health_tool, icon: Heart, color: 'text-red-500', bg: 'bg-red-100', desc: t.health_tool_desc },
    { id: 'finance', label: t.finance_tool, icon: Wallet, color: 'text-emerald-500', bg: 'bg-emerald-100', desc: t.finance_tool_desc },
    { id: 'learning', label: t.learning_tool, icon: GraduationCap, color: 'text-indigo-500', bg: 'bg-indigo-100', desc: t.learning_tool_desc },
    { id: 'travel', label: t.travel_tool, icon: Map, color: 'text-teal-500', bg: 'bg-teal-100', desc: t.travel_tool_desc },
  ];

  const renderResult = () => {
    if (!result) return null;
    if (result.error) return <div className="p-4 bg-red-50 text-red-500 rounded-2xl">Error generating content.</div>;

    switch (activeModule) {
      case 'cooking':
        return (
          <div className="space-y-4">
            <h3 className="text-2xl font-black text-slate-800">{result.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-orange-50 p-6 rounded-3xl">
                <h4 className="font-bold mb-2 flex items-center gap-2"><ChefHat className="w-4 h-4" /> Ingredients</h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {result.ingredients?.map((item: string, i: number) => <li key={i}>{item}</li>)}
                </ul>
              </div>
              <div className="bg-white p-6 rounded-3xl border border-slate-100">
                <h4 className="font-bold mb-2 flex items-center gap-2"><ListChecks className="w-4 h-4" /> Instructions</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  {result.instructions?.map((item: string, i: number) => <li key={i}>{item}</li>)}
                </ol>
              </div>
            </div>
          </div>
        );
      case 'tasks':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-black text-slate-800">{result.taskName}</h3>
            <div className="space-y-2">
              {result.steps?.map((step: string, i: number) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">{i+1}</div>
                  <span className="text-sm font-medium">{step}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">Estimated: {result.estimatedTime}</p>
          </div>
        );
      case 'health':
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['morning', 'afternoon', 'evening'].map(time => (
              <div key={time} className="p-6 bg-rose-50 rounded-3xl border border-rose-100">
                <h4 className="font-bold capitalize mb-2">{time}</h4>
                <p className="text-sm text-slate-600">{result[time]}</p>
              </div>
            ))}
          </div>
        );
      default:
        return <div className="whitespace-pre-wrap text-slate-700">{JSON.stringify(result, null, 2)}</div>;
    }
  };

  if (activeModule) {
    const mod = coreModules.find(m => m.id === activeModule);
    return (
      <div className="animate-fade-in space-y-8 pb-20">
        <button 
          onClick={() => { setActiveModule(null); setResult(null); setInputValue(''); }}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-bold transition-colors bg-white px-4 py-2 rounded-xl border border-slate-100 shadow-sm"
        >
          <ChevronLeft className="w-5 h-5 rtl:rotate-180" />
          {t.back_to_hub}
        </button>

        <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-xl relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-6">
              <div className={`p-4 rounded-2xl ${mod?.bg} ${mod?.color} shadow-sm`}>
                {mod && <mod.icon className="w-8 h-8" />}
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-800">{mod?.label}</h2>
                <p className="text-sm text-slate-500 font-medium">{mod?.desc}</p>
              </div>
            </div>

            <div className="space-y-4">
              {activeModule !== 'health' && (
                <textarea 
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={user.language === 'fa' ? "جزئیات یا درخواست خود را اینجا بنویسید..." : "وردەکارییەکان لێرە بنووسە..."}
                  className="w-full h-32 bg-slate-50 border border-slate-200 rounded-[2rem] px-6 py-4 focus:ring-4 focus:ring-primary-50/50 outline-none transition-all text-lg leading-relaxed shadow-inner"
                />
              )}
              
              <button 
                onClick={handleToolAction}
                disabled={isLoading || (activeModule !== 'health' && !inputValue.trim())}
                className="w-full bg-primary-600 text-white py-4 rounded-2xl font-black text-lg hover:bg-primary-700 disabled:opacity-50 transition-all shadow-xl shadow-primary-500/20 flex items-center justify-center gap-3"
              >
                {isLoading ? <Loader2 className="animate-spin w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
                {t.generate_now}
              </button>
            </div>
          </div>
        </div>

        {result && (
          <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm animate-slide-up relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary-400 to-indigo-500"></div>
            <h3 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-2">
              <Zap className="w-6 h-6 text-amber-500" />
              {user.language === "fa" ? "نتیجه هوشمند هاورێ" : "ئەنجامی زیرەکانەی هاوڕێ"}
            </h3>
            {renderResult()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-16 pb-24 animate-fade-in">
      <section>
        <div className="mb-10 text-right">
            <div className="flex items-center justify-end gap-3 mb-4">
                <h2 className="text-3xl font-black text-slate-800">{t.tools_portal}</h2>
                <div className="p-4 bg-primary-100 text-primary-600 rounded-[1.5rem] shadow-sm"><LayoutGrid className="w-8 h-8" /></div>
            </div>
            <p className="text-slate-500 text-base font-medium leading-relaxed max-w-2xl mr-auto">
              {t.tools_portal_desc}
            </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {coreModules.map((mod) => (
            <button 
              key={mod.id} 
              onClick={() => setActiveModule(mod.id as ModuleType)} 
              className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-2xl hover:scale-[1.02] transition-all text-right group flex flex-col items-start h-full"
            >
              <div className={`w-16 h-16 rounded-2xl ${mod.bg} ${mod.color} flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform shadow-sm`}>
                <mod.icon className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">{mod.label}</h3>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 line-clamp-2">
                {mod.desc}
              </p>
              <div className="mt-auto flex items-center gap-2 text-primary-500 text-xs font-black uppercase tracking-wider group-hover:translate-x-[-8px] transition-transform">
                <span>{t.continue}</span>
                <ArrowRight className="w-4 h-4 rotate-180" />
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ModulesHub;
